export default {
  name: 'Teleport',

  props: {
    to: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      target: null,
      placeholder: document.createComment('teleport-placeholder'),
      mounted: false,
      // Track all teleports to the same target
      siblings: []
    }
  },

  watch: {
    disabled(disabled) {
      if (disabled) {
        this.moveTo(this.placeholder)
      } else {
        this.moveToTarget()
      }
    },

    to(newTarget, oldTarget) {
      if (this.disabled) return

      const newTargetEl = document.querySelector(newTarget)
      if (!newTargetEl) {
        console.warn(`Target element ${newTarget} not found`)
        return
      }

      // Remove from old target's siblings list
      if (this.target) {
        const key = this.getTargetKey(this.target)
        const siblings = this.getSiblings(key)
        const index = siblings.indexOf(this)
        if (index > -1) {
          siblings.splice(index, 1)
        }
      }

      this.target = newTargetEl
      this.moveToTarget()
    }
  },

  created() {
    // Initialize static map to track teleports if it doesn't exist
    if (!this.$root.$teleportGroups) {
      this.$root.$teleportGroups = new Map()
    }
  },

  mounted() {
    this.target = document.querySelector(this.to)
    if (!this.target) {
      console.warn(`Target element ${this.to} not found`)
      return
    }

    // Insert placeholder
    this.$el.parentNode.insertBefore(this.placeholder, this.$el)

    // Add this instance to siblings list
    const key = this.getTargetKey(this.target)
    const siblings = this.getSiblings(key)
    siblings.push(this)

    if (!this.disabled) {
      this.moveToTarget()
    }
    this.mounted = true
  },

  beforeDestroy() {
    // Remove from siblings list
    const key = this.getTargetKey(this.target)
    const siblings = this.getSiblings(key)
    const index = siblings.indexOf(this)
    if (index > -1) {
      siblings.splice(index, 1)
    }

    // Move content back to original position before destroying
    if (this.$el.parentNode !== this.placeholder.parentNode) {
      this.moveTo(this.placeholder)
    }

    // Remove placeholder
    if (this.placeholder.parentNode) {
      this.placeholder.parentNode.removeChild(this.placeholder)
    }
  },

  methods: {
    getTargetKey(target) {
      // Use the selector as the key for grouping teleports
      return this.to
    },

    getSiblings(key) {
      if (!this.$root.$teleportGroups.has(key)) {
        this.$root.$teleportGroups.set(key, [])
      }
      return this.$root.$teleportGroups.get(key)
    },

    moveToTarget() {
      if (!this.target) return

      const key = this.getTargetKey(this.target)
      const siblings = this.getSiblings(key)
      const index = siblings.indexOf(this)

      // Find the next sibling element in the target
      let nextSibling = null
      for (let i = index + 1; i < siblings.length; i++) {
        const siblingEl = siblings[i].$el
        if (siblingEl.parentNode === this.target) {
          nextSibling = siblingEl
          break
        }
      }

      this.moveTo(this.target, nextSibling)
    },

    moveTo(target, nextSibling = null) {
      if (!this.$el || !target) return

      const parent = target.parentNode
      if (parent) {
        if (target === this.placeholder) {
          parent.insertBefore(this.$el, target)
        } else {
          // If nextSibling is provided, insert before it
          // Otherwise append to the end
          if (nextSibling) {
            target.insertBefore(this.$el, nextSibling)
          } else {
            target.appendChild(this.$el)
          }
        }
      }
    }
  },

  render(h) {
    return this.$slots.default ? this.$slots.default[0] : null
  }
}